import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, nullableText, paginated } from './common.js';
import { ShipmentTypeSchema } from './enums.js';
import { OrderStatusCodeSchema } from './statuses.js';
import { MoneySchema } from './money.js';
/**
 * ISH NAVBATI — admin panelning asosiy ekrani.
 *
 * Buyurtma ro'yxati EMAS. Operator bir xil turdagi vazifalarni ketma-ket
 * bajaradi: o'nta buyurtmani tasdiqlaydi, keyin o'nta to'lovni tekshiradi.
 * Kontekst almashtirish — operator vaqtini yeydigan asosiy narsa
 * (docs/02-MAHSULOT.md → "Admin ish navbati").
 *
 * Buyurtma ro'yxati qidiruv va mijoz qo'ng'irog'i uchun qoladi.
 */
/**
 * Navbat guruhlari. Tartib — operatorning ish kuni tartibi, alifbo emas.
 *
 * Hujjatda sakkizta guruh sanalgan. Ikkitasi QO'SHILDI, sababi bilan:
 *
 * - `PACK` — SHOP oqimida `PAID -> PACKED` o'tishi bor va u har jo'natma
 *   uchun alohida bajariladi, lekin hujjatdagi sakkizlikda yo'q edi. Guruhsiz
 *   qoldirilsa to'langan do'kon buyurtmalari navbatdan butunlay yo'qoladi.
 * - `EXCEPTION` — `ON_HOLD`, `FAILED_DELIVERY`, `RETURN_REQUESTED`,
 *   `PARTIALLY_SHIPPED`. Bular oqimdan chiqib ketgan jo'natmalar; hech bir
 *   guruhga tushmasa, ular haqida hech kim eslatmaydi.
 *
 * Xitoy oqimining oraliq bosqichlari (`SUPPLIER_ORDERED -> SUPPLIER_SHIPPED`
 * va h.k.) ATAYLAB guruh emas: ular partiya (`Consignment`) bo'yicha ommaviy
 * ko'chiriladi, jo'natma-jo'natma emas.
 */
export const WORK_QUEUE_BUCKETS = [
    'CONFIRM',
    'SEND_CARD',
    'CHECK_PAYMENT',
    'PACK',
    'ORDER_SUPPLIER',
    'CUSTOMS',
    'RECEIVE_SHOP',
    'READY_TO_SHIP',
    'ETA_OVERDUE',
    'EXCEPTION',
];
export const WorkQueueBucketSchema = z.enum(WORK_QUEUE_BUCKETS);
export const WORK_QUEUE_BUCKET_DEFS = [
    {
        bucket: 'CONFIRM',
        labelUz: 'Tasdiqlash',
        labelRu: 'Подтвердить',
        statuses: ['NEW'],
        flow: null,
        action: 'CONFIRMED',
        permission: 'order.confirm',
    },
    {
        bucket: 'SEND_CARD',
        labelUz: 'Karta yuborish',
        labelRu: 'Отправить карту',
        statuses: ['CONFIRMED'],
        flow: null,
        action: 'AWAITING_PAYMENT',
        permission: 'order.change_status',
    },
    {
        bucket: 'CHECK_PAYMENT',
        labelUz: "To'lovni tekshirish",
        labelRu: 'Проверить оплату',
        statuses: ['AWAITING_PAYMENT'],
        flow: null,
        action: 'PAID',
        permission: 'payment.confirm',
    },
    {
        bucket: 'PACK',
        labelUz: "Yig'ish",
        labelRu: 'Собрать',
        statuses: ['PAID'],
        flow: 'SHOP',
        action: 'PACKED',
        permission: 'order.change_status',
    },
    {
        bucket: 'ORDER_SUPPLIER',
        labelUz: "Ta'minotchiga berish",
        labelRu: 'Заказать у поставщика',
        statuses: ['PAID'],
        flow: 'CHINA',
        action: 'SUPPLIER_ORDERED',
        permission: 'order.change_status_logistics',
    },
    {
        bucket: 'CUSTOMS',
        labelUz: 'Bojxonadan chiqarish',
        labelRu: 'Растаможить',
        statuses: ['ARRIVED_BORDER', 'CUSTOMS_DECLARED'],
        flow: 'CHINA',
        // Ikki statusdan ikki xil keyingi qadam bor, shuning uchun bitta
        // `action` yozib bo'lmaydi. E2E test har ikkisini alohida tekshiradi.
        action: null,
        permission: 'order.change_status_logistics',
    },
    {
        bucket: 'RECEIVE_SHOP',
        labelUz: "Do'konga qabul",
        labelRu: 'Принять на склад',
        statuses: ['CUSTOMS_CLEARED'],
        flow: 'CHINA',
        action: 'AT_WAREHOUSE',
        permission: 'order.change_status_logistics',
    },
    {
        bucket: 'READY_TO_SHIP',
        labelUz: 'Yetkazishga tayyor',
        labelRu: 'Готово к доставке',
        statuses: ['PACKED', 'READY'],
        flow: null,
        action: 'OUT_FOR_DELIVERY',
        permission: 'order.assign_courier',
    },
    {
        bucket: 'ETA_OVERDUE',
        labelUz: "Muddati o'tib ketgan",
        labelRu: 'Просрочено',
        // Status bo'yicha emas, `etaTo < hozir` bo'yicha yig'iladi. Ro'yxat
        // bo'sh — filtr `isOverdue` orqali.
        statuses: [],
        flow: null,
        action: null,
        permission: 'order.view_all',
    },
    {
        bucket: 'EXCEPTION',
        labelUz: "E'tibor talab qiladi",
        labelRu: 'Требует внимания',
        statuses: ['ON_HOLD', 'PARTIALLY_SHIPPED', 'FAILED_DELIVERY', 'RETURN_REQUESTED'],
        flow: null,
        action: null,
        permission: 'order.view_all',
    },
];
/** `bucket` bo'yicha ta'rifni topish. Ro'yxat bo'ylab qidirish takrorlanmasin. */
const DEFS_BY_BUCKET = new Map(WORK_QUEUE_BUCKET_DEFS.map((def) => [def.bucket, def]));
export function workQueueBucketDef(bucket) {
    const def = DEFS_BY_BUCKET.get(bucket);
    // `WorkQueueBucket` tipi ro'yxatdan kelib chiqadi, shuning uchun bu
    // shart amalda bajarilmaydi — lekin ta'rif tushib qolsa jim qolmaslik kerak.
    if (def === undefined)
        throw new Error(`Ish navbati guruhi ta'rifsiz: ${bucket}`);
    return def;
}
/** Bitta guruh sanog'i. */
export const WorkQueueCountSchema = z.object({
    bucket: WorkQueueBucketSchema,
    count: z.number().int().nonnegative(),
    /**
     * Guruhdagi eng uzoq kutayotgan jo'natma qachondan beri turibdi.
     * Guruh bo'sh bo'lsa `null`. Operator shu bo'yicha ustuvorlik beradi.
     */
    oldestWaitingSince: IsoDateTimeSchema.nullable(),
});
/**
 * Navbat boshi — barcha guruhlar sanog'i.
 *
 * Ruxsati yetmagan guruhlar javobga UMUMAN kirmaydi: nol ko'rsatib
 * qo'yish "u yerda ish yo'q" degan yolg'on ma'no beradi.
 */
export const WorkQueueSummaryResponseSchema = z.object({
    buckets: z.array(WorkQueueCountSchema),
    generatedAt: IsoDateTimeSchema,
});
/** Navbatdagi bitta qator. Operatorga qaror qilish uchun yetarli ma'lumot. */
export const WorkQueueItemSchema = z.object({
    shipmentId: IdSchema,
    orderId: IdSchema,
    orderNumber: z.string(),
    type: ShipmentTypeSchema,
    statusCode: OrderStatusCodeSchema,
    statusLabelUz: z.string(),
    statusLabelRu: nullableText(80),
    shopId: IdSchema.nullable(),
    shopNameUz: z.string().nullable(),
    customerName: z.string(),
    customerPhone: z.string(),
    regionNameUz: z.string().nullable(),
    itemCount: z.number().int().positive(),
    /** Buyurtmaning umumiy summasi, tiyinda (string bo'lib yuriladi). */
    orderTotal: MoneySchema,
    etaFrom: IsoDateTimeSchema.nullable(),
    etaTo: IsoDateTimeSchema.nullable(),
    /** ETA necha marta siljigan — ko'p siljish ta'minotchi muammosi signali. */
    etaShiftCount: z.number().int().nonnegative(),
    /** `etaTo` o'tib ketganmi. Serverda hisoblanadi, klient soatiga ishonilmaydi. */
    isOverdue: z.boolean(),
    /** Shu statusga qachon o'tgan — navbatda qancha turgani. */
    waitingSince: IsoDateTimeSchema,
    orderCreatedAt: IsoDateTimeSchema,
});
export const WorkQueueItemsResponseSchema = paginated(WorkQueueItemSchema);
/**
 * Ro'yxat so'rovi.
 *
 * `shopId` — do'konga biriktirilgan xodim uchun server MAJBURAN qo'yadi;
 * bu filtr Owner/Admin uchun ixtiyoriy toraytirish.
 */
export const WorkQueueQuerySchema = z
    .object({
    limit: z.coerce.number().int().min(1).max(100).default(25),
    cursor: z.string().max(255).optional(),
    shopId: IdSchema.optional(),
})
    .strict();
//# sourceMappingURL=work-queue.js.map